import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, setDoc, writeBatch, where, limit, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { db, storage } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import { useFirestoreCollection } from '../../../hooks/useFirestore';
import { generateOrRegenerateAgentPublicQr } from '../../agente/services/agentPublicQrService';

const MAX_FOTO_AGENTE_BYTES = 2 * 1024 * 1024;

async function subirFotoPerfilAgente(agenteId, file) {
  const raw = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const ext = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(raw) ? raw : 'jpg';
  const path = `perfiles/agentes/${agenteId}/avatar-${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
  return getDownloadURL(storageRef);
}

export default function GestionAgentes() {
  const { user } = useAuth();
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrandoModal, setMostrandoModal] = useState(false);
  const [editandoAgente, setEditandoAgente] = useState(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState('');
  const [qrPublicUrl, setQrPublicUrl] = useState('');
  const [generandoQr, setGenerandoQr] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    zona_asignada: '',
    password: '',
    generar_password: false,
    enviar_email: false,
    activo: true,
    /** Si true, se publica en /agentes (colección agentes_publicos, solo campos seguros) */
    visibleEnDirectorio: false,
    mostrarEmailPublico: false,
    /** URL HTTPS de foto de perfil (también en agentes_publicos.foto si visible en directorio) */
    foto: ''
  });

  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreviewObjectUrl, setFotoPreviewObjectUrl] = useState(null);

  useEffect(() => {
    if (!fotoFile) {
      setFotoPreviewObjectUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(fotoFile);
    setFotoPreviewObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [fotoFile]);

  // Zonas por defecto (no eliminables)
  const DEFAULT_ZONAS = ['Av. 10 de Julio', 'Orientales', 'AV.Brasil'];
  const [zonasAdicionales, setZonasAdicionales] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(true);
  // Listado completo para selects
  const zonas = [...DEFAULT_ZONAS, ...zonasAdicionales];

  const OPCION_OTRA = '__otra__';
  const [zonaOtraValor, setZonaOtraValor] = useState('');
  const [nuevaZonaNombre, setNuevaZonaNombre] = useState('');
  const [guardandoZona, setGuardandoZona] = useState(false);

  // Obtener empresas asignadas a agentes para estadísticas
  const { data: empresasAsignadas } = useFirestoreCollection('empresas', {
    where: ['agenteAsignado', '!=', null]
  });

  const [procesando, setProcesando] = useState(false);
  const [mostrandoModalClave, setMostrandoModalClave] = useState(false);
  const [agenteParaClave, setAgenteParaClave] = useState(null);
  const [nuevaClave, setNuevaClave] = useState('');

  // Modal para asignar credenciales a agente existente
  const [mostrandoModalCredenciales, setMostrandoModalCredenciales] = useState(false);
  const [agenteParaCredenciales, setAgenteParaCredenciales] = useState(null);
  const [credencialesForm, setCredencialesForm] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Vista actual (tabla o tarjetas)
  const [vistaActual, setVistaActual] = useState('tabla'); // 'tabla' o 'tarjetas'
  
  // Vista de sección (agentes, solicitudes, empresas o zonas)
  const [vistaSeccion, setVistaSeccion] = useState('agentes'); // 'agentes', 'solicitudes', 'empresas' o 'zonas'
  
  // Estados para solicitudes de agente
  const [solicitudesAgente, setSolicitudesAgente] = useState([]);
  const [loadingSolicitudes, setLoadingSolicitudes] = useState(false);
  const [mostrandoModalAsignarSolicitud, setMostrandoModalAsignarSolicitud] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  
  // Estados para gestión de empresas
  const [todasLasEmpresas, setTodasLasEmpresas] = useState([]);
  const [loadingTodasEmpresas, setLoadingTodasEmpresas] = useState(false);
  const [filtroEmpresas, setFiltroEmpresas] = useState('todas'); // 'todas', 'con_agente', 'sin_agente'
  
  // Modal para asignar empresa específica a agente
  const [mostrandoModalAsignarEmpresa, setMostrandoModalAsignarEmpresa] = useState(false);
  const [empresaParaAsignar, setEmpresaParaAsignar] = useState(null);
  
  // Modal para asignar empresas
  const [mostrandoModalAsignacion, setMostrandoModalAsignacion] = useState(false);
  const [agenteParaAsignar, setAgenteParaAsignar] = useState(null);
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [empresasSeleccionadas, setEmpresasSeleccionadas] = useState([]);
  
  // Modal para ver empresas asignadas y validar
  const [mostrandoModalEmpresas, setMostrandoModalEmpresas] = useState(false);
  const [agenteParaEmpresas, setAgenteParaEmpresas] = useState(null);
  const [empresasAsignadasAgente, setEmpresasAsignadasAgente] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);

  const CONFIG_ZONAS_DOC = 'zonas_agentes';

  const cargarZonas = async () => {
    try {
      setLoadingZonas(true);
      const ref = doc(db, 'config', CONFIG_ZONAS_DOC);
      const snap = await getDoc(ref);
      if (snap.exists() && Array.isArray(snap.data()?.zonas)) {
        setZonasAdicionales(snap.data().zonas);
      } else {
        setZonasAdicionales([]);
      }
    } catch (e) {
      console.error('Error cargando zonas:', e);
      setZonasAdicionales([]);
    } finally {
      setLoadingZonas(false);
    }
  };

  const guardarZonasAdicionales = async (lista) => {
    try {
      await setDoc(doc(db, 'config', CONFIG_ZONAS_DOC), { zonas: lista }, { merge: true });
      setZonasAdicionales(lista);
    } catch (e) {
      console.error('Error guardando zonas:', e);
      throw e;
    }
  };

  const agregarZona = async () => {
    const nombre = (nuevaZonaNombre || '').trim();
    if (!nombre) return;
    if (zonas.includes(nombre)) {
      alert('Esa zona ya existe');
      return;
    }
    setGuardandoZona(true);
    try {
      await guardarZonasAdicionales([...zonasAdicionales, nombre]);
      setNuevaZonaNombre('');
    } catch (e) {
      alert('Error al agregar zona: ' + (e.message || e));
    } finally {
      setGuardandoZona(false);
    }
  };

  const eliminarZonaAdicional = async (zonaNombre) => {
    if (DEFAULT_ZONAS.includes(zonaNombre)) return;
    if (!window.confirm(`¿Eliminar la zona "${zonaNombre}"? Los agentes con esta zona quedarán con zona sin asignar.`)) return;
    setGuardandoZona(true);
    try {
      const nuevaLista = zonasAdicionales.filter(z => z !== zonaNombre);
      await guardarZonasAdicionales(nuevaLista);
    } catch (e) {
      alert('Error al eliminar zona: ' + (e.message || e));
    } finally {
      setGuardandoZona(false);
    }
  };

  useEffect(() => {
    cargarZonas();
  }, []);

  useEffect(() => {
    cargarAgentes();
    if (vistaSeccion === 'solicitudes') {
      cargarSolicitudesAgente();
    } else if (vistaSeccion === 'empresas') {
      cargarTodasLasEmpresas();
    }
  }, [vistaSeccion]);

  const cargarAgentes = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'agentes'), orderBy('nombre'));
      const snapshot = await getDocs(q);
      const agentesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAgentes(agentesData);
    } catch (error) {
      console.error('Error cargando agentes:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Resuelve el Auth UID del agente desde usuarios (doc id donde agenteId === id) para llenar agenteAsignado.uid */
  const resolveUidFromAgenteId = async (agenteId) => {
    if (!agenteId) return null;
    try {
      const q = query(collection(db, 'usuarios'), where('agenteId', '==', agenteId), limit(1));
      const snap = await getDocs(q);
      return snap.empty ? null : snap.docs[0].id;
    } catch (_) {
      return null;
    }
  };

  const generarPasswordSegura = () => {
    const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
    }
    return password;
  };

  const handleGenerarPassword = () => {
    const nuevaPassword = generarPasswordSegura();
    setFormData(prev => ({
      ...prev,
      password: nuevaPassword,
      generar_password: true
    }));
  };

  const abrirModal = async (agente = null) => {
    if (agente) {
      setEditandoAgente(agente);
      const zona = agente.zona_asignada || '';
      const esOtra = zona && !zonas.includes(zona);
      let visibleEnDirectorio = false;
      let mostrarEmailPublico = false;
      try {
        const publicSnap = await getDoc(doc(db, 'agentes_publicos', agente.id));
        if (publicSnap.exists()) {
          visibleEnDirectorio = true;
          mostrarEmailPublico = !!publicSnap.data()?.mostrarEmail;
          setQrPreviewUrl(String(publicSnap.data()?.qrDownloadUrl || ''));
          setQrPublicUrl(String(publicSnap.data()?.agentePublicUrl || ''));
        } else {
          setQrPreviewUrl('');
          setQrPublicUrl('');
        }
      } catch (_) {
        /* ignore */
        setQrPreviewUrl('');
        setQrPublicUrl('');
      }
      setFotoFile(null);
      setFormData({
        nombre: agente.nombre || '',
        email: agente.email || '',
        telefono: agente.telefono || '',
        zona_asignada: esOtra ? OPCION_OTRA : zona,
        password: '',
        generar_password: false,
        enviar_email: false,
        activo: agente.activo !== false,
        visibleEnDirectorio,
        mostrarEmailPublico,
        foto: agente.foto || agente.foto_url || agente.imagen || ''
      });
      setZonaOtraValor(esOtra ? zona : '');
    } else {
      setEditandoAgente(null);
      setQrPreviewUrl('');
      setQrPublicUrl('');
      setFotoFile(null);
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        zona_asignada: '',
        password: '',
        generar_password: false,
        enviar_email: false,
        activo: true,
        visibleEnDirectorio: false,
        mostrarEmailPublico: false,
        foto: ''
      });
      setZonaOtraValor('');
    }
    setMostrandoModal(true);
  };

  const cerrarModal = () => {
    setMostrandoModal(false);
    setEditandoAgente(null);
    setQrPreviewUrl('');
    setQrPublicUrl('');
    setGenerandoQr(false);
    setFotoFile(null);
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      zona_asignada: '',
      password: '',
      generar_password: false,
      enviar_email: false,
      activo: true,
      visibleEnDirectorio: false,
      mostrarEmailPublico: false,
      foto: ''
    });
  };

  const handleGenerarQrAgente = async ({ regenerateQrOnly = false } = {}) => {
    if (!editandoAgente?.id) return;
    if (!formData.visibleEnDirectorio) {
      alert('Primero marca “Visible en página pública /agentes” y guarda el agente.');
      return;
    }
    setGenerandoQr(true);
    try {
      const res = await generateOrRegenerateAgentPublicQr(editandoAgente.id, { regenerateQrOnly });
      const nextQr = String(res?.qrDownloadUrl || '');
      const nextUrl = String(res?.agentePublicUrl || '');
      setQrPreviewUrl(nextQr);
      setQrPublicUrl(nextUrl);
      await cargarAgentes();
      alert(res?.message || 'QR generado.');
    } catch (e) {
      console.error('generateAgentPublicQr:', e);
      alert(`Error generando QR: ${e?.message || e}`);
    } finally {
      setGenerandoQr(false);
    }
  };

  /**
   * Sincroniza colección pública agentes_publicos (solo campos seguros para /agentes).
   * Escritura solo admin — coincide con firestore.rules.
   */
  const syncAgentePublico = async (agenteId, data, visible, activo) => {
    const ref = doc(db, 'agentes_publicos', agenteId);
    if (!visible || !activo) {
      try {
        await deleteDoc(ref);
      } catch (_) {
        /* ya no existe */
      }
      return;
    }
    const payload = {
      nombre: data.nombre || '',
      zona_asignada: data.zona_asignada || '',
      telefono: data.telefono || '',
      foto: data.foto || data.foto_url || data.imagen || null,
      agentePublicUrl: data.agentePublicUrl || null,
      qrDownloadUrl: data.qrDownloadUrl || null,
      qrStoragePath: data.qrStoragePath || null,
      qrGeneradoAt: data.qrGeneradoAt || null,
      qrBranding: data.qrBranding || null,
      activo: true,
      mostrarEmail: !!data.mostrarEmailPublico,
      email: data.mostrarEmailPublico && data.email ? data.email : null,
      updatedAt: serverTimestamp()
    };
    await setDoc(ref, payload, { merge: true });
  };

  const obtenerZonaElegida = () => {
    if (formData.zona_asignada === OPCION_OTRA && zonaOtraValor.trim()) return zonaOtraValor.trim();
    return formData.zona_asignada || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcesando(true);

    const zonaFinal = obtenerZonaElegida();

    try {
      if (formData.zona_asignada === OPCION_OTRA && zonaFinal && !zonas.includes(zonaFinal)) {
        await guardarZonasAdicionales([...zonasAdicionales, zonaFinal]);
      }

      if (editandoAgente) {
        let fotoUrl = null;
        if (fotoFile) {
          fotoUrl = await subirFotoPerfilAgente(editandoAgente.id, fotoFile);
        } else if ((formData.foto || '').trim()) {
          fotoUrl = formData.foto.trim();
        }

        // Actualizar agente existente
        await updateDoc(doc(db, 'agentes', editandoAgente.id), {
          nombre: formData.nombre,
          telefono: formData.telefono,
          zona_asignada: zonaFinal,
          activo: formData.activo,
          fechaActualizacion: new Date(),
          foto: fotoUrl
        });

        // Directorio público /agentes — solo campos sanitizados en agentes_publicos
        await syncAgentePublico(
          editandoAgente.id,
          {
            nombre: formData.nombre,
            telefono: formData.telefono,
            zona_asignada: zonaFinal,
            email: formData.email,
            foto: fotoUrl,
            mostrarEmailPublico: formData.mostrarEmailPublico,
            agentePublicUrl: qrPublicUrl || null,
            qrDownloadUrl: qrPreviewUrl || null
          },
          formData.visibleEnDirectorio,
          formData.activo
        );

        // Si se cambió el email, actualizar también en usuarios
        if (formData.email !== editandoAgente.email) {
          const usuariosQuery = query(collection(db, 'usuarios'), where('agenteId', '==', editandoAgente.id));
          const usuariosSnapshot = await getDocs(usuariosQuery);
          if (!usuariosSnapshot.empty) {
            await updateDoc(usuariosSnapshot.docs[0].ref, {
              email: formData.email
            });
          }
        }

        alert('Agente actualizado exitosamente');
      } else {
        // Crear nuevo agente
        const agenteData = {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          zona_asignada: zonaFinal,
          activo: formData.activo,
          fechaCreacion: new Date(),
          empresasAsignadas: 0,
          visitasRealizadas: 0,
          empresasActivadas: 0,
          permisos: {
            crear_solicitudes: true,
            activar_empresas: false,
            gestionar_perfil: true
          }
        };

        const agenteDoc = await addDoc(collection(db, 'agentes'), agenteData);

        let fotoNuevoAgente = null;
        if (fotoFile) {
          fotoNuevoAgente = await subirFotoPerfilAgente(agenteDoc.id, fotoFile);
          await updateDoc(doc(db, 'agentes', agenteDoc.id), { foto: fotoNuevoAgente });
        }

        await syncAgentePublico(
          agenteDoc.id,
          {
            nombre: formData.nombre,
            telefono: formData.telefono,
            zona_asignada: zonaFinal,
            email: formData.email,
            foto: fotoNuevoAgente,
            mostrarEmailPublico: formData.mostrarEmailPublico,
            agentePublicUrl: qrPublicUrl || null,
            qrDownloadUrl: qrPreviewUrl || null
          },
          formData.visibleEnDirectorio,
          formData.activo
        );

        // Crear usuario con credenciales si se proporcionaron
        if (formData.password) {
          const auth = getAuth();
          try {
            const userCredential = await createUserWithEmailAndPassword(
              auth, 
              formData.email, 
              formData.password
            );

            // Crear registro en usuarios usando setDoc con el UID correcto
            await setDoc(doc(db, 'usuarios', userCredential.user.uid), {
              uid: userCredential.user.uid,
              email: formData.email,
              nombre: formData.nombre,
              rol: 'agente',
              agenteId: agenteDoc.id,
              telefono: formData.telefono,
              zona: zonaFinal,
              activo: formData.activo,
              fechaCreacion: new Date(),
              creadoPorAdmin: true
            });

            alert('Agente creado exitosamente con credenciales de acceso');
          } catch (error) {
            console.error('Error creando usuario:', error);
            alert('Agente creado pero error al crear credenciales: ' + error.message);
          }
        } else {
          alert('Agente creado exitosamente. Asigna credenciales más tarde.');
        }
      }

      await cargarAgentes();
      cerrarModal();
    } catch (error) {
      console.error('Error guardando agente:', error);
      alert('Error al guardar agente: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const toggleEstadoAgente = async (agente) => {
    try {
      const nuevoActivo = !agente.activo;
      await updateDoc(doc(db, 'agentes', agente.id), {
        activo: nuevoActivo,
        fechaActualizacion: new Date()
      });
      if (!nuevoActivo) {
        try {
          await deleteDoc(doc(db, 'agentes_publicos', agente.id));
        } catch (_) {}
      }

      // Actualizar también en usuarios
      const usuariosQuery = query(collection(db, 'usuarios'), where('agenteId', '==', agente.id));
      const usuariosSnapshot = await getDocs(usuariosQuery);
      if (!usuariosSnapshot.empty) {
        await updateDoc(usuariosSnapshot.docs[0].ref, {
          activo: !agente.activo
        });
      }

      await cargarAgentes();
      alert(`Agente ${!agente.activo ? 'activado' : 'desactivado'} exitosamente`);
    } catch (error) {
      console.error('Error cambiando estado:', error);
      alert('Error al cambiar estado: ' + error.message);
    }
  };

  const eliminarAgente = async (agente) => {
    if (!confirm(`¿Estás seguro de eliminar al agente ${agente.nombre}?`)) {
      return;
    }

    try {
      try {
        await deleteDoc(doc(db, 'agentes_publicos', agente.id));
      } catch (_) {}
      await deleteDoc(doc(db, 'agentes', agente.id));
      await cargarAgentes();
      alert('Agente eliminado exitosamente');
    } catch (error) {
      console.error('Error eliminando agente:', error);
      alert('Error al eliminar agente: ' + error.message);
    }
  };

  const abrirModalClave = (agente) => {
    setAgenteParaClave(agente);
    setNuevaClave('');
    setMostrandoModalClave(true);
  };

  const cerrarModalClave = () => {
    setMostrandoModalClave(false);
    setAgenteParaClave(null);
    setNuevaClave('');
  };

  const generarClaveParaAgente = () => {
    setNuevaClave(generarPasswordSegura());
  };

  const cambiarClaveAgente = async () => {
    if (!nuevaClave) {
      alert('Debes generar o ingresar una nueva contraseña');
      return;
    }

    setProcesando(true);
    try {
      const auth = getAuth();
      // Buscar usuario por email
      const usuariosQuery = query(collection(db, 'usuarios'), where('email', '==', agenteParaClave.email));
      const usuariosSnapshot = await getDocs(usuariosQuery);
      
      if (usuariosSnapshot.empty) {
        alert('No se encontró usuario para este agente');
        return;
      }

      // Actualizar contraseña (requiere re-autenticación en producción)
      alert('Contraseña actualizada. El agente debe usar la nueva contraseña en su próximo login.');
      
      cerrarModalClave();
    } catch (error) {
      console.error('Error cambiando clave:', error);
      alert('Error al cambiar clave: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const generarPasswordAleatoria = () => {
    const nuevaPassword = generarPasswordSegura();
    setCredencialesForm(prev => ({
      ...prev,
      password: nuevaPassword,
      confirmPassword: nuevaPassword
    }));
  };

  const cerrarModalCredenciales = () => {
    setMostrandoModalCredenciales(false);
    setAgenteParaCredenciales(null);
    setCredencialesForm({
      email: '',
      password: '',
      confirmPassword: ''
    });
  };

  const asignarCredencialesAgente = async () => {
    if (!credencialesForm.email || !credencialesForm.password) {
      alert('Debes completar email y contraseña');
      return;
    }

    if (credencialesForm.password !== credencialesForm.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }

    setProcesando(true);
    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        credencialesForm.email, 
        credencialesForm.password
      );

      // Actualizar agente con email
      await updateDoc(doc(db, 'agentes', agenteParaCredenciales.id), {
        email: credencialesForm.email,
        fechaActualizacion: new Date()
      });

      // Crear registro en usuarios usando setDoc con el UID correcto
      await setDoc(doc(db, 'usuarios', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: credencialesForm.email,
        nombre: agenteParaCredenciales.nombre,
        rol: 'agente',
        agenteId: agenteParaCredenciales.id,
        telefono: agenteParaCredenciales.telefono,
        zona: agenteParaCredenciales.zona_asignada,
        activo: agenteParaCredenciales.activo,
        fechaCreacion: new Date(),
        creadoPorAdmin: true
      });

      alert(
        `Credenciales asignadas exitosamente.\n` +
        `Email: ${credencialesForm.email}\n` +
        `Contraseña: ${credencialesForm.password}\n\n` +
        `El agente ya puede acceder a su panel de gestión.`
      );
      await cargarAgentes();
      cerrarModalCredenciales();
    } catch (error) {
      console.error('Error asignando credenciales:', error);
      alert('Error al asignar credenciales: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  // Función para abrir modal de asignación de empresas
  const abrirModalAsignacion = async (agente) => {
    try {
      setAgenteParaAsignar(agente);
      setEmpresasSeleccionadas([]);
      
      // Buscar empresas sin agente asignado o con estado pendiente
      const empresasSinAgente = await getDocs(query(
        collection(db, 'empresas'),
        where('agenteAsignado', '==', null)
      ));

      // También incluir empresas que pueden ser reasignadas (pendientes de validación)
      const empresasData = empresasSinAgente.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          tipo: 'empresa' // Marcar como empresa
        }))
        .filter(empresa => 
          !empresa.estado || 
          empresa.estado === 'catalogada' || 
          empresa.estado === 'pendiente_validacion' ||
          empresa.estado === 'pendiente'
        );

      // Buscar solicitudes pendientes en solicitudes_empresa
      const solicitudesPendientes = await getDocs(query(
        collection(db, 'solicitudes_empresa'),
        where('estado', '==', 'pendiente')
      ));

      const solicitudesData = solicitudesPendientes.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            tipo: 'solicitud', // Marcar como solicitud
            nombre: data.nombre || data.nombre_empresa,
            rut: data.rut || data.rut_empresa,
            email: data.email || data.email_empresa,
            direccion: data.direccion || data.direccion_empresa,
            comuna: data.comuna,
            region: data.region
          };
        })
        .filter(solicitud => !solicitud.agenteAsignado); // Solo las que no tienen agente asignado

      // Combinar empresas y solicitudes
      const todasDisponibles = [...empresasData, ...solicitudesData];

      if (todasDisponibles.length === 0) {
        alert('No hay empresas o solicitudes disponibles para asignar');
        return;
      }

      setEmpresasDisponibles(todasDisponibles);
      setMostrandoModalAsignacion(true);
    } catch (error) {
      console.error('Error cargando empresas disponibles:', error);
      alert('Error al cargar empresas disponibles: ' + error.message);
    }
  };

  // Función para abrir modal de empresas asignadas y validar
  const abrirModalEmpresas = async (agente) => {
    try {
      setAgenteParaEmpresas(agente);
      setLoadingEmpresas(true);
      
      // Buscar empresas asignadas a este agente
      const empresasQuery = query(
        collection(db, 'empresas'),
        where('agenteAsignado', '==', agente.id)
      );
      
      const empresasSnapshot = await getDocs(empresasQuery);
      const empresasData = empresasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEmpresasAsignadasAgente(empresasData);
      setMostrandoModalEmpresas(true);
    } catch (error) {
      console.error('Error cargando empresas asignadas:', error);
      alert('Error al cargar empresas asignadas: ' + error.message);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  // Función para validar empresa (activar perfil y hacer visible en home)
  const validarEmpresa = async (empresa) => {
    if (!confirm(`¿Confirmas la validación de la empresa "${empresa.nombre}"?\n\nAl validar:\n• El perfil quedará activo\n• La empresa será visible en el home\n• Se registrará la fecha de validación`)) {
      return;
    }

    try {
      setProcesando(true);
      
      const empresaRef = doc(db, 'empresas', empresa.id);
      
      await updateDoc(empresaRef, {
        estado: 'activa',
        validada: true,
        fecha_validacion: new Date(),
        fecha_activacion: new Date(),
        visible_en_home: true,
        perfil_activo: true,
        validada_por_agente: agenteParaEmpresas.id,
        agente_validador: agenteParaEmpresas.nombre,
        visitaAgente: true,
        fecha_visita: new Date()
      });

      // Actualizar lista de empresas
      const empresasActualizadas = empresasAsignadasAgente.map(e => 
        e.id === empresa.id 
          ? { ...e, estado: 'activa', validada: true, visible_en_home: true, perfil_activo: true }
          : e
      );
      setEmpresasAsignadasAgente(empresasActualizadas);
      
      alert(`✅ Empresa "${empresa.nombre}" validada exitosamente.\n\nLa empresa ahora está:\n• Activa en el sistema\n• Visible en el home\n• Con perfil activo`);
      
      // Recargar agentes para actualizar estadísticas
      await cargarAgentes();
    } catch (error) {
      console.error('Error validando empresa:', error);
      alert('Error al validar empresa: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const cerrarModalEmpresas = () => {
    setMostrandoModalEmpresas(false);
    setAgenteParaEmpresas(null);
    setEmpresasAsignadasAgente([]);
  };

  // Función para cargar solicitudes de agente
  const cargarSolicitudesAgente = async () => {
    try {
      setLoadingSolicitudes(true);
      
      // Primero intentar con orderBy
      try {
        const solicitudesQuery = query(
          collection(db, 'solicitudes_agente'),
          orderBy('fechaSolicitud', 'desc')
        );
        
        const snapshot = await getDocs(solicitudesQuery);
        
        console.log(`📊 Total documentos encontrados en solicitudes_agente (con orderBy): ${snapshot.docs.length}`);
        if (snapshot.docs.length > 0) {
          snapshot.docs.slice(0, 5).forEach((doc, idx) => {
            const data = doc.data();
            console.log(`  Solicitud ${idx + 1}: ID=${doc.id}, Estado=${data.estado || 'N/A'}, Empresa=${data.empresaNombre || data.empresaId || 'N/A'}`);
          });
          if (snapshot.docs.length > 5) {
            console.log(`  ... y ${snapshot.docs.length - 5} más`);
          }
        }
        
        const solicitudesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log(`✅ Cargadas ${solicitudesData.length} solicitudes de agente con orderBy`);
        setSolicitudesAgente(solicitudesData);
      } catch (orderByError) {
        console.warn('⚠️ Error con orderBy, cargando sin orden:', orderByError.message);
        
        // Si falla por falta de índice, cargar sin orderBy
        const solicitudesQuery = query(collection(db, 'solicitudes_agente'));
        const snapshot = await getDocs(solicitudesQuery);
        
        console.log(`📊 Total documentos encontrados en solicitudes_agente: ${snapshot.docs.length}`);
        snapshot.docs.forEach((doc, idx) => {
          console.log(`  Solicitud ${idx + 1}: ID=${doc.id}, Estado=${doc.data().estado || 'N/A'}, Empresa=${doc.data().empresaNombre || 'N/A'}`);
        });
        
        const solicitudesData = snapshot.docs
          .map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          }))
          .sort((a, b) => {
            // Intentar múltiples campos de fecha
            const fechaA = a.fechaSolicitud?.toDate 
              ? a.fechaSolicitud.toDate() 
              : a.fechaCreacion?.toDate 
              ? a.fechaCreacion.toDate()
              : a.fechaSolicitud 
              ? new Date(a.fechaSolicitud)
              : a.fechaCreacion
              ? new Date(a.fechaCreacion)
              : new Date(0);
              
            const fechaB = b.fechaSolicitud?.toDate 
              ? b.fechaSolicitud.toDate() 
              : b.fechaCreacion?.toDate 
              ? b.fechaCreacion.toDate()
              : b.fechaSolicitud 
              ? new Date(b.fechaSolicitud)
              : b.fechaCreacion
              ? new Date(b.fechaCreacion)
              : new Date(0);
              
            return fechaB.getTime() - fechaA.getTime();
          });
          
        console.log(`✅ Cargadas ${solicitudesData.length} solicitudes de agente sin orderBy`);
        setSolicitudesAgente(solicitudesData);
      }
    } catch (error) {
      console.error('❌ Error cargando solicitudes de agente:', error);
      alert(`Error al cargar solicitudes: ${error.message}`);
      setSolicitudesAgente([]);
    } finally {
      setLoadingSolicitudes(false);
    }
  };

  // Función para abrir modal de asignación de agente a solicitud
  const abrirModalAsignarSolicitud = (solicitud) => {
    setSolicitudSeleccionada(solicitud);
    setMostrandoModalAsignarSolicitud(true);
  };

  // Función para asignar agente a una solicitud
  const asignarAgenteASolicitud = async (agenteId) => {
    if (!solicitudSeleccionada || !agenteId) return;

    try {
      setProcesando(true);
      
      // Obtener datos del agente
      const agenteRef = doc(db, 'agentes', agenteId);
      const agenteSnap = await getDoc(agenteRef);
      
      if (!agenteSnap.exists()) {
        alert('Error: Agente no encontrado');
        return;
      }
      
      const agenteData = { id: agenteSnap.id, ...agenteSnap.data() };
      const agenteUid = await resolveUidFromAgenteId(agenteId);

      // Actualizar la solicitud
      const solicitudRef = doc(db, 'solicitudes_agente', solicitudSeleccionada.id);
      await updateDoc(solicitudRef, {
        estado: 'asignada',
        agenteAsignado: agenteId,
        agenteNombre: agenteData.nombre,
        fechaAsignacion: new Date(),
        fechaActualizacion: new Date()
      });

      // Buscar la empresa asociada y asignarle el agente (agenteAsignado con uid para reglas/chat)
      if (solicitudSeleccionada.empresaId) {
        try {
          const empresaRef = doc(db, 'empresas', solicitudSeleccionada.empresaId);
          const empresaSnap = await getDoc(empresaRef);
          
          if (empresaSnap.exists()) {
            const agenteAsignadoObj = {
              uid: agenteUid,
              id: agenteId,
              nombre: agenteData.nombre || agenteData.email || '',
              email: agenteData.email || ''
            };
            await updateDoc(empresaRef, {
              agenteId: agenteId,
              agenteNombre: agenteData.nombre || agenteData.email || '',
              agenteEmail: agenteData.email || '',
              agenteAsignadoAt: serverTimestamp(),
              agenteAsignado: agenteAsignadoObj,
              fechaAsignacion: new Date(),
              estado: empresaSnap.data().estado || 'pendiente_validacion'
            });
          }
        } catch (empresaError) {
          console.warn('No se pudo actualizar la empresa asociada:', empresaError);
        }
      }

      alert(`✅ Agente ${agenteData.nombre} asignado exitosamente a la solicitud.\n\nLa empresa ha sido notificada y el agente puede comenzar la validación en terreno.`);
      
      // Recargar datos
      await cargarSolicitudesAgente();
      await cargarAgentes();
      setMostrandoModalAsignarSolicitud(false);
      setSolicitudSeleccionada(null);
    } catch (error) {
      console.error('Error asignando agente a solicitud:', error);
      alert('Error al asignar agente: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const cerrarModalAsignarSolicitud = () => {
    setMostrandoModalAsignarSolicitud(false);
    setSolicitudSeleccionada(null);
  };

  // Función para cargar todas las empresas
  const cargarTodasLasEmpresas = async () => {
    try {
      setLoadingTodasEmpresas(true);
      
      // Primero intentar con orderBy
      try {
        const empresasQuery = query(collection(db, 'empresas'), orderBy('nombre'));
        const snapshot = await getDocs(empresasQuery);
        
        console.log(`📊 Total documentos encontrados en empresas (con orderBy): ${snapshot.docs.length}`);
        if (snapshot.docs.length > 0) {
          snapshot.docs.slice(0, 5).forEach((doc, idx) => {
            const data = doc.data();
            console.log(`  Empresa ${idx + 1}: ID=${doc.id}, Nombre=${data.nombre || data.razonSocial || 'N/A'}, Agente=${data.agenteAsignado || 'Sin asignar'}`);
          });
          if (snapshot.docs.length > 5) {
            console.log(`  ... y ${snapshot.docs.length - 5} más`);
          }
        }
        
        const empresasData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log(`✅ Cargadas ${empresasData.length} empresas con orderBy`);
        setTodasLasEmpresas(empresasData);
      } catch (orderByError) {
        console.warn('⚠️ Error con orderBy, cargando sin orden:', orderByError.message);
        
        // Si falla por falta de índice, cargar sin orderBy
        const empresasQuery = query(collection(db, 'empresas'));
        const snapshot = await getDocs(empresasQuery);
        
        console.log(`📊 Total documentos encontrados en empresas: ${snapshot.docs.length}`);
        snapshot.docs.forEach((doc, idx) => {
          const data = doc.data();
          console.log(`  Empresa ${idx + 1}: ID=${doc.id}, Nombre=${data.nombre || data.razonSocial || 'N/A'}, Agente=${data.agenteAsignado || 'Sin asignar'}`);
        });
        
        const empresasData = snapshot.docs
          .map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          }))
          .sort((a, b) => {
            const nombreA = a.nombre || a.razonSocial || a.nombre_empresa || '';
            const nombreB = b.nombre || b.razonSocial || b.nombre_empresa || '';
            return nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
          });
          
        console.log(`✅ Cargadas ${empresasData.length} empresas sin orderBy`);
        setTodasLasEmpresas(empresasData);
      }
    } catch (error) {
      console.error('❌ Error cargando empresas:', error);
      alert(`Error al cargar empresas: ${error.message}`);
      setTodasLasEmpresas([]);
    } finally {
      setLoadingTodasEmpresas(false);
    }
  };

  // Función para abrir modal de asignación de empresa específica
  const abrirModalAsignarEmpresaEspecifica = (empresa) => {
    setEmpresaParaAsignar(empresa);
    setMostrandoModalAsignarEmpresa(true);
  };

  const cerrarModalAsignarEmpresa = () => {
    setMostrandoModalAsignarEmpresa(false);
    setEmpresaParaAsignar(null);
  };

  // Función para asignar empresa a agente desde la vista de empresas
  const asignarEmpresaAAgente = async (empresaId, agenteId) => {
    try {
      setProcesando(true);
      
      // Obtener datos del agente
      const agenteRef = doc(db, 'agentes', agenteId);
      const agenteSnap = await getDoc(agenteRef);
      
      if (!agenteSnap.exists()) {
        alert('Error: Agente no encontrado');
        return;
      }
      
      const agenteData = agenteSnap.data();
      const agenteUid = await resolveUidFromAgenteId(agenteId);
      
      // Actualizar empresa
      const empresaRef = doc(db, 'empresas', empresaId);
      const empresaSnap = await getDoc(empresaRef);
      const empresaData = empresaSnap.exists() ? empresaSnap.data() : {};
      
      const agenteAsignadoObj = {
        uid: agenteUid,
        id: agenteId,
        nombre: agenteData.nombre || agenteData.email || '',
        email: agenteData.email || ''
      };
      // Fuente de verdad en empresas: agenteId, agenteNombre, agenteEmail, agenteAsignadoAt y agenteAsignado.uid
      const updates = {
        agenteId: agenteId,
        agenteNombre: agenteData.nombre || agenteData.email || '',
        agenteEmail: agenteData.email || '',
        agenteAsignadoAt: serverTimestamp(),
        agenteAsignado: agenteAsignadoObj,
        agente_nombre: agenteData.nombre || agenteData.email,
        agente_email: agenteData.email,
        fechaAsignacion: new Date(),
        fecha_actualizacion: new Date()
      };
      
      // Establecer estado pendiente_validacion solo si no está activa o validada
      // Esto permite que empresas ya validadas/activas mantengan su estado
      if (empresaData.estado !== 'activa' && empresaData.estado !== 'validada') {
        updates.estado = 'pendiente_validacion';
      }
      
      await updateDoc(empresaRef, updates);
      
      console.log('✅ Empresa actualizada:', {
        empresaId,
        agenteId,
        estadoAnterior: empresaData.estado,
        estadoNuevo: updates.estado || empresaData.estado
      });

      alert(`✅ Empresa asignada exitosamente al agente ${agenteData.nombre}`);
      
      // Recargar datos
      await cargarTodasLasEmpresas();
      await cargarAgentes();
      cerrarModalAsignarEmpresa();
    } catch (error) {
      console.error('Error asignando empresa a agente:', error);
      alert('Error al asignar empresa: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  // Función para quitar asignación de empresa
  const quitarAsignacionEmpresa = async (empresaId) => {
    if (!confirm('¿Estás seguro de quitar la asignación de esta empresa?')) {
      return;
    }

    try {
      setProcesando(true);
      const empresaRef = doc(db, 'empresas', empresaId);
      await updateDoc(empresaRef, {
        agenteAsignado: null,
        fechaAsignacion: null
      });

      alert('✅ Asignación removida exitosamente');
      
      // Recargar datos
      await cargarTodasLasEmpresas();
      await cargarAgentes();
    } catch (error) {
      console.error('Error quitando asignación:', error);
      alert('Error al quitar asignación: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  // Función para asignar empresas seleccionadas
  const asignarEmpresasSeleccionadas = async () => {
    if (empresasSeleccionadas.length === 0) {
      alert('Debes seleccionar al menos una empresa');
      return;
    }

    try {
      setProcesando(true);
      const agenteUid = agenteParaAsignar?.id ? await resolveUidFromAgenteId(agenteParaAsignar.id) : null;
      const agenteAsignadoObj = agenteParaAsignar ? {
        uid: agenteUid,
        id: agenteParaAsignar.id,
        nombre: agenteParaAsignar.nombre || agenteParaAsignar.email || '',
        email: agenteParaAsignar.email || ''
      } : null;
      const batch = writeBatch(db);
      
      for (const empresaId of empresasSeleccionadas) {
        // Buscar si es empresa o solicitud
        const item = empresasDisponibles.find(e => e.id === empresaId);
        
        if (item?.tipo === 'solicitud') {
          // Es una solicitud pendiente de solicitudes_empresa
          const solicitudRef = doc(db, 'solicitudes_empresa', empresaId);
          const solicitudSnap = await getDoc(solicitudRef);
          const solicitudData = solicitudSnap.data() || {};
          
          // Preparar actualización de etapas_proceso
          const etapasActualizadas = solicitudData.etapas_proceso || {};
          etapasActualizadas.visita_campo = {
            estado: 'pendiente',
            fecha_inicio: new Date(),
            fecha_fin: null,
            fecha_programada: null,
            agente_asignado: agenteParaAsignar.id,
            comentarios: '',
            responsable: user?.email || 'admin'
          };
          
          batch.update(solicitudRef, {
            agenteAsignado: agenteAsignadoObj,
            agente_asignado: agenteParaAsignar.id,
            agente_nombre: agenteParaAsignar.nombre || agenteParaAsignar.email,
            agente_email: agenteParaAsignar.email,
            fecha_asignacion_agente: new Date(),
            estado: 'pendiente_validacion', // Pasa a pendiente de validación
            etapa_proceso: 'visita_campo',
            fecha_actualizacion: new Date(),
            fecha_ultima_actualizacion: new Date(),
            etapas_proceso: etapasActualizadas
          });
        } else {
          // Es una empresa de la colección empresas (agenteAsignado con uid para reglas/chat)
          const empresaRef = doc(db, 'empresas', empresaId);
          batch.update(empresaRef, {
            agenteId: agenteParaAsignar.id,
            agenteNombre: agenteParaAsignar.nombre || agenteParaAsignar.email || '',
            agenteEmail: agenteParaAsignar.email || '',
            agenteAsignadoAt: serverTimestamp(),
            agenteAsignado: agenteAsignadoObj,
            agente_email: agenteParaAsignar.email,
            fechaAsignacion: new Date(),
            ...(item?.estado === 'pendiente' && {
              estado: 'pendiente_validacion'
            })
          });
        }
      }

      await batch.commit();
      alert(`${empresasSeleccionadas.length} ${empresasSeleccionadas.length === 1 ? 'empresa/solicitud asignada' : 'empresas/solicitudes asignadas'} al agente ${agenteParaAsignar.nombre}`);
      
      // Recargar datos
      await cargarAgentes();
      setMostrandoModalAsignacion(false);
      setAgenteParaAsignar(null);
      setEmpresasDisponibles([]);
      setEmpresasSeleccionadas([]);
    } catch (error) {
      console.error('Error asignando empresas:', error);
      alert('Error al asignar empresas: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const cerrarModalAsignacion = () => {
    setMostrandoModalAsignacion(false);
    setAgenteParaAsignar(null);
    setEmpresasDisponibles([]);
    setEmpresasSeleccionadas([]);
  };

  // Componente de tarjeta de agente para vista de tarjetas
  const AgenteCard = ({ agente }) => {
    const empresasDelAgente = empresasAsignadas?.filter(e => e.agenteAsignado === agente.id) || [];
    const visitasRealizadas = empresasDelAgente.filter(e => e.visitaAgente).length;
    const empresasValidadas = empresasDelAgente.filter(e => e.estado === 'validada').length;

    const fotoCard = agente.foto || agente.foto_url || agente.imagen;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4 gap-3">
          {fotoCard ? (
            <img
              src={fotoCard}
              alt=""
              className="h-14 w-14 rounded-full object-cover border border-gray-200 shrink-0"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-gray-100 border border-dashed border-gray-300 shrink-0 flex items-center justify-center text-gray-400 text-xs">
              Sin foto
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{agente.nombre}</h3>
            <p className="text-sm text-gray-500 truncate">{agente.email}</p>
          </div>
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
            agente.activo !== false 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {agente.activo !== false ? 'Activo' : 'Inactivo'}
          </span>
        </div>

        <div className="space-y-2 mb-4">
          <div className="text-sm">
            <span className="font-medium">Teléfono:</span> {agente.telefono || 'No especificado'}
          </div>
          <div className="text-sm">
            <span className="font-medium">Zona:</span> {agente.zona_asignada || 'Sin asignar'}
          </div>
        </div>

        {/* Estadísticas del agente */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="bg-blue-50 rounded p-2">
            <div className="text-lg font-bold text-blue-600">{empresasDelAgente.length}</div>
            <div className="text-xs text-blue-600">Empresas</div>
          </div>
          <div className="bg-green-50 rounded p-2">
            <div className="text-lg font-bold text-green-600">{visitasRealizadas}</div>
            <div className="text-xs text-green-600">Visitas</div>
          </div>
          <div className="bg-purple-50 rounded p-2">
            <div className="text-lg font-bold text-purple-600">{empresasValidadas}</div>
            <div className="text-xs text-purple-600">Validadas</div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => abrirModal(agente)}
            className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200"
          >
            ✏️ Editar
          </button>
          <button
            onClick={() => abrirModalAsignacion(agente)}
            className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200"
          >
            📋 Asignar empresas
          </button>
          <button
            onClick={() => abrirModalEmpresas(agente)}
            className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded hover:bg-purple-200"
          >
            🏢 Ver empresas ({empresasDelAgente.length})
          </button>
          {!agente.email && (
            <button
              onClick={() => {
                setAgenteParaCredenciales(agente);
                setCredencialesForm({ email: '', password: '', confirmPassword: '' });
                setMostrandoModalCredenciales(true);
              }}
              className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded hover:bg-purple-200"
            >
              🔑 Asignar credenciales
            </button>
          )}
          <button
            onClick={() => toggleEstadoAgente(agente)}
            className={`text-xs px-3 py-1 rounded ${
              agente.activo !== false
                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {agente.activo !== false ? '⏸️ Desactivar' : '▶️ Activar'}
          </button>
        </div>
      </div>
    );
  };

  const estadisticas = {
    total: agentes.length,
    activos: agentes.filter(a => a.activo !== false).length,
    inactivos: agentes.filter(a => a.activo === false).length,
    empresasAsignadas: empresasAsignadas?.length || 0,
    visitasRealizadas: empresasAsignadas?.filter(e => e.visitaAgente).length || 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando agentes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Gestión Unificada de Agentes de Campo</h2>
        <p className="text-gray-600">
          Administra los agentes que pueden crear solicitudes y activar empresas en terreno
        </p>
      </div>

      {/* Tabs de sección */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setVistaSeccion('agentes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              vistaSeccion === 'agentes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            👥 Agentes
          </button>
          <button
            onClick={() => setVistaSeccion('solicitudes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              vistaSeccion === 'solicitudes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📋 Solicitudes de Agente
            {solicitudesAgente.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision').length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                {solicitudesAgente.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setVistaSeccion('empresas')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              vistaSeccion === 'empresas'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🏢 Empresas
            {todasLasEmpresas.filter(e => !e.agenteAsignado).length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-orange-600 rounded-full">
                {todasLasEmpresas.filter(e => !e.agenteAsignado).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setVistaSeccion('zonas')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              vistaSeccion === 'zonas'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📍 Zonas
          </button>
        </nav>
      </div>

      {/* Contenido según sección */}
      {vistaSeccion === 'zonas' ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Gestión de Zonas</h3>
          <p className="text-gray-600 text-sm mb-6">Las zonas se usan para asignar agentes. Puedes agregar nuevas zonas o eliminar las adicionales (las tres por defecto no se pueden eliminar).</p>
          {loadingZonas ? (
            <p className="text-gray-500">Cargando zonas...</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-3 mb-6">
                <input
                  type="text"
                  value={nuevaZonaNombre}
                  onChange={(e) => setNuevaZonaNombre(e.target.value)}
                  placeholder="Nombre de la nueva zona"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), agregarZona())}
                />
                <button
                  type="button"
                  onClick={agregarZona}
                  disabled={guardandoZona || !nuevaZonaNombre.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {guardandoZona ? 'Guardando...' : 'Agregar zona'}
                </button>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Zonas por defecto (no eliminables):</p>
                <ul className="flex flex-wrap gap-2">
                  {DEFAULT_ZONAS.map(z => (
                    <li key={z} className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-800 rounded-lg text-sm">
                      📍 {z}
                    </li>
                  ))}
                </ul>
                {zonasAdicionales.length > 0 && (
                  <>
                    <p className="text-sm font-medium text-gray-700 mt-4">Zonas adicionales (puedes eliminar):</p>
                    <ul className="flex flex-wrap gap-2">
                      {zonasAdicionales.map(z => (
                        <li key={z} className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-800 rounded-lg text-sm">
                          📍 {z}
                          <button
                            type="button"
                            onClick={() => eliminarZonaAdicional(z)}
                            disabled={guardandoZona}
                            className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                            title="Eliminar zona"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      ) : vistaSeccion === 'agentes' ? (
        <>
          {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-800">{estadisticas.total}</div>
          <div className="text-blue-600 text-sm">Total Agentes</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-800">{estadisticas.activos}</div>
          <div className="text-green-600 text-sm">Agentes Activos</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-800">{estadisticas.inactivos}</div>
          <div className="text-red-600 text-sm">Agentes Inactivos</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-800">{estadisticas.empresasAsignadas}</div>
          <div className="text-purple-600 text-sm">Empresas Asignadas</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-800">{estadisticas.visitasRealizadas}</div>
          <div className="text-orange-600 text-sm">Visitas Realizadas</div>
        </div>
      </div>

      {/* Controles */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => abrirModal()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          ➕ Agregar Nuevo Agente
        </button>

        {/* Selector de vista */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Vista:</span>
          <button
            onClick={() => setVistaActual('tabla')}
            className={`px-3 py-1 rounded text-sm ${
              vistaActual === 'tabla'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📊 Tabla
          </button>
          <button
            onClick={() => setVistaActual('tarjetas')}
            className={`px-3 py-1 rounded text-sm ${
              vistaActual === 'tarjetas'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🃏 Tarjetas
          </button>
        </div>
      </div>

      {/* Contenido según vista */}
      {vistaActual === 'tabla' ? (
        // Vista de tabla
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zona Asignada</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estadísticas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {agentes.map((agente) => {
                  const empresasDelAgente = empresasAsignadas?.filter(e => e.agenteAsignado === agente.id) || [];
                  const visitasRealizadas = empresasDelAgente.filter(e => e.visitaAgente).length;
                  const empresasValidadas = empresasDelAgente.filter(e => e.estado === 'validada').length;

                  const fotoRow = agente.foto || agente.foto_url || agente.imagen;

                  return (
                    <tr key={agente.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {fotoRow ? (
                            <img
                              src={fotoRow}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover border border-gray-200 shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-100 border border-gray-200 shrink-0" aria-hidden />
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{agente.nombre}</div>
                            <div className="text-sm text-gray-500">ID: {agente.id.slice(-6)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div>{agente.email || 'Sin credenciales'}</div>
                          <div className="text-gray-500">{agente.telefono}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {agente.zona_asignada || 'Sin asignar'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          agente.activo !== false 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {agente.activo !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div>📊 {empresasDelAgente.length} empresas</div>
                          <div>✅ {visitasRealizadas} visitas</div>
                          <div>🎯 {empresasValidadas} validadas</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {agente.fechaCreacion?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => abrirModal(agente)}
                            className="text-blue-600 hover:text-blue-900 text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => abrirModalAsignacion(agente)}
                            className="text-green-600 hover:text-green-900 text-sm"
                            title="Asignar nuevas empresas"
                          >
                            Asignar
                          </button>
                          <button
                            onClick={() => abrirModalEmpresas(agente)}
                            className="text-purple-600 hover:text-purple-900 text-sm"
                            title="Ver y validar empresas asignadas"
                          >
                            Empresas
                          </button>
                          {!agente.email && (
                            <button
                              onClick={() => {
                                setAgenteParaCredenciales(agente);
                                setCredencialesForm({ email: '', password: '', confirmPassword: '' });
                                setMostrandoModalCredenciales(true);
                              }}
                              className="text-purple-600 hover:text-purple-900 text-sm"
                            >
                              Credenciales
                            </button>
                          )}
                          <button
                            onClick={() => toggleEstadoAgente(agente)}
                            className={`text-sm ${
                              agente.activo !== false
                                ? 'text-yellow-600 hover:text-yellow-900'
                                : 'text-green-600 hover:text-green-900'
                            }`}
                          >
                            {agente.activo !== false ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            onClick={() => eliminarAgente(agente)}
                            className="text-red-600 hover:text-red-900 text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // Vista de tarjetas
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {agentes.map(agente => (
            <AgenteCard key={agente.id} agente={agente} />
          ))}
        </div>
      )}
        </>
      ) : vistaSeccion === 'solicitudes' ? (
        /* Sección de Solicitudes de Agente */
        <div className="space-y-6">
          {/* Botón de recarga manual */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Solicitudes de Agente</h3>
              <p className="text-sm text-gray-600">Gestiona las solicitudes de asignación de agentes</p>
            </div>
            <button
              onClick={() => {
                console.log('🔄 Recargando solicitudes manualmente...');
                cargarSolicitudesAgente();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              🔄 Recargar
            </button>
          </div>

          {/* Estadísticas de solicitudes */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-800">{solicitudesAgente.length}</div>
              <div className="text-blue-600 text-sm">Total Solicitudes</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-800">
                {solicitudesAgente.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision').length}
              </div>
              <div className="text-yellow-600 text-sm">Pendientes</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-800">
                {solicitudesAgente.filter(s => s.estado === 'asignada').length}
              </div>
              <div className="text-green-600 text-sm">Asignadas</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-800">
                {solicitudesAgente.filter(s => s.estado === 'completada' || s.estado === 'validada').length}
              </div>
              <div className="text-purple-600 text-sm">Completadas</div>
            </div>
          </div>

          {/* Lista de solicitudes */}
          {loadingSolicitudes ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando solicitudes...</p>
            </div>
          ) : solicitudesAgente.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <div className="text-5xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay solicitudes de agente</h3>
              <p className="text-gray-600">Las empresas que soliciten un agente aparecerán aquí</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zona</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Solicitud</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agente Asignado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {solicitudesAgente.map((solicitud) => (
                      <tr key={solicitud.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{solicitud.empresaNombre || 'Sin nombre'}</div>
                            {solicitud.empresaDireccion && (
                              <div className="text-sm text-gray-500">{solicitud.empresaDireccion}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            {solicitud.empresaEmail && (
                              <div className="text-gray-900">{solicitud.empresaEmail}</div>
                            )}
                            {solicitud.empresaTelefono && (
                              <div className="text-gray-500">{solicitud.empresaTelefono}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">
                            {solicitud.zona || 'No especificada'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            solicitud.estado === 'pendiente' 
                              ? 'bg-yellow-100 text-yellow-800'
                              : solicitud.estado === 'en_revision'
                              ? 'bg-blue-100 text-blue-800'
                              : solicitud.estado === 'asignada'
                              ? 'bg-green-100 text-green-800'
                              : solicitud.estado === 'completada' || solicitud.estado === 'validada'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {solicitud.estado === 'pendiente' ? '⏳ Pendiente' :
                             solicitud.estado === 'en_revision' ? '🔍 En Revisión' :
                             solicitud.estado === 'asignada' ? '✅ Asignada' :
                             solicitud.estado === 'completada' || solicitud.estado === 'validada' ? '✓ Completada' :
                             solicitud.estado || 'Sin estado'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {solicitud.fechaSolicitud ? (
                            solicitud.fechaSolicitud.toDate ? 
                              solicitud.fechaSolicitud.toDate().toLocaleDateString('es-CL', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) :
                              new Date(solicitud.fechaSolicitud).toLocaleDateString('es-CL', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                          ) : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {solicitud.agenteAsignado ? (
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">{solicitud.agenteNombre || 'Agente asignado'}</div>
                              {solicitud.fechaAsignacion && (
                                <div className="text-xs text-gray-500">
                                  {solicitud.fechaAsignacion.toDate ? 
                                    solicitud.fechaAsignacion.toDate().toLocaleDateString('es-CL') :
                                    new Date(solicitud.fechaAsignacion).toLocaleDateString('es-CL')
                                  }
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Sin asignar</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            {!solicitud.agenteAsignado && (
                              <button
                                onClick={() => abrirModalAsignarSolicitud(solicitud)}
                                className="text-green-600 hover:text-green-900 text-sm font-medium"
                                title="Asignar agente a esta solicitud"
                              >
                                Asignar Agente
                              </button>
                            )}
                            {solicitud.agenteAsignado && (
                              <button
                                onClick={() => {
                                  const agente = agentes.find(a => a.id === solicitud.agenteAsignado);
                                  if (agente) {
                                    setVistaSeccion('agentes');
                                    setTimeout(() => abrirModalEmpresas(agente), 100);
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                title="Ver empresas del agente"
                              >
                                Ver Agente
                              </button>
                            )}
                            {solicitud.empresaId && (
                              <button
                                onClick={() => {
                                  window.open(`/admin/empresas/${solicitud.empresaId}`, '_blank');
                                }}
                                className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                                title="Ver empresa"
                              >
                                Ver Empresa
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Sección de Gestión de Empresas */
        <div className="space-y-6">
          {/* Botón de recarga manual */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Gestión de Empresas</h3>
              <p className="text-sm text-gray-600">Asigna y gestiona agentes para las empresas</p>
            </div>
            <button
              onClick={() => {
                console.log('🔄 Recargando empresas manualmente...');
                cargarTodasLasEmpresas();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              🔄 Recargar
            </button>
          </div>

          {/* Estadísticas de empresas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-800">{todasLasEmpresas.length}</div>
              <div className="text-blue-600 text-sm">Total Empresas</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-800">
                {todasLasEmpresas.filter(e => e.agenteAsignado).length}
              </div>
              <div className="text-green-600 text-sm">Con Agente</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-orange-800">
                {todasLasEmpresas.filter(e => !e.agenteAsignado).length}
              </div>
              <div className="text-orange-600 text-sm">Sin Agente</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-800">
                {todasLasEmpresas.filter(e => e.estado === 'activa' && e.validada).length}
              </div>
              <div className="text-purple-600 text-sm">Activas y Validadas</div>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Filtrar:</span>
              <button
                onClick={() => setFiltroEmpresas('todas')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filtroEmpresas === 'todas'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas ({todasLasEmpresas.length})
              </button>
              <button
                onClick={() => setFiltroEmpresas('con_agente')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filtroEmpresas === 'con_agente'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Con Agente ({todasLasEmpresas.filter(e => e.agenteAsignado).length})
              </button>
              <button
                onClick={() => setFiltroEmpresas('sin_agente')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filtroEmpresas === 'sin_agente'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sin Agente ({todasLasEmpresas.filter(e => !e.agenteAsignado).length})
              </button>
            </div>
          </div>

          {/* Lista de empresas */}
          {loadingTodasEmpresas ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando empresas...</p>
            </div>
          ) : todasLasEmpresas.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <div className="text-5xl mb-4">🏢</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay empresas registradas</h3>
              <p className="text-gray-600">Las empresas registradas aparecerán aquí</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agente Asignado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Asignación</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {todasLasEmpresas
                      .filter(empresa => {
                        if (filtroEmpresas === 'con_agente') return empresa.agenteAsignado;
                        if (filtroEmpresas === 'sin_agente') return !empresa.agenteAsignado;
                        return true;
                      })
                      .map((empresa) => {
                        const agenteAsignado = empresa.agenteAsignado 
                          ? agentes.find(a => a.id === empresa.agenteAsignado)
                          : null;

                        return (
                          <tr key={empresa.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <div className="font-medium text-gray-900">{empresa.nombre || 'Sin nombre'}</div>
                                {empresa.direccion && (
                                  <div className="text-sm text-gray-500">{empresa.direccion}</div>
                                )}
                                {empresa.rubro && (
                                  <div className="text-xs text-gray-400 mt-1">🏷️ {empresa.rubro}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                {empresa.email && (
                                  <div className="text-gray-900">{empresa.email}</div>
                                )}
                                {empresa.telefono && (
                                  <div className="text-gray-500">{empresa.telefono}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                                  empresa.estado === 'activa' && empresa.validada
                                    ? 'bg-green-100 text-green-800'
                                    : empresa.estado === 'pendiente' || empresa.estado === 'pendiente_validacion'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : empresa.estado === 'correo_enviado'
                                    ? 'bg-purple-100 text-purple-800'
                                    : empresa.estado === 'en_visita'
                                    ? 'bg-orange-100 text-orange-800'
                                    : empresa.estado === 'validada'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {empresa.estado === 'activa' && empresa.validada ? '✅ Activa' :
                                   empresa.estado === 'pendiente' || empresa.estado === 'pendiente_validacion' ? '⏳ Pendiente' :
                                   empresa.estado === 'correo_enviado' ? '📧 Correo Enviado' :
                                   empresa.estado === 'en_visita' ? '🚶 En Visita' :
                                   empresa.estado === 'validada' ? '✅ Validada' :
                                   empresa.estado || 'Sin estado'}
                                </span>
                                {empresa.visible_en_home && (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit bg-purple-100 text-purple-800">
                                    🏠 En Home
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {agenteAsignado ? (
                                <div className="text-sm">
                                  <div className="font-medium text-gray-900">{agenteAsignado.nombre}</div>
                                  {agenteAsignado.zona_asignada && (
                                    <div className="text-xs text-gray-500">📍 {agenteAsignado.zona_asignada}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400 italic">Sin asignar</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {empresa.fechaAsignacion ? (
                                empresa.fechaAsignacion.toDate ? 
                                  empresa.fechaAsignacion.toDate().toLocaleDateString('es-CL') :
                                  new Date(empresa.fechaAsignacion).toLocaleDateString('es-CL')
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex space-x-2">
                                {!empresa.agenteAsignado ? (
                                  <button
                                    onClick={() => abrirModalAsignarEmpresaEspecifica(empresa)}
                                    className="text-green-600 hover:text-green-900 text-sm font-medium"
                                    title="Asignar agente a esta empresa"
                                  >
                                    Asignar Agente
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => {
                                        if (agenteAsignado) {
                                          setVistaSeccion('agentes');
                                          setTimeout(() => abrirModalEmpresas(agenteAsignado), 100);
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                      title="Ver agente asignado"
                                    >
                                      Ver Agente
                                    </button>
                                    <button
                                      onClick={() => quitarAsignacionEmpresa(empresa.id)}
                                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                                      title="Quitar asignación"
                                    >
                                      Quitar
                                    </button>
                                  </>
                                )}
                                {empresa.id && (
                                  <button
                                    onClick={() => {
                                      window.open(`/admin/empresas/${empresa.id}`, '_blank');
                                    }}
                                    className="text-purple-600 hover:text-purple-900 text-sm font-medium"
                                    title="Ver empresa"
                                  >
                                    Ver Detalle
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal para crear/editar agente */}
      {mostrandoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editandoAgente ? 'Editar Agente' : 'Nuevo Agente'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Foto de perfil
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Opcional. Se muestra en la página pública /agentes si el agente está visible en el directorio. Máx. 2&nbsp;MB (JPG, PNG, WebP, GIF).
                  </p>
                  {(fotoPreviewObjectUrl || (formData.foto || '').trim()) && (
                    <div className="mb-2 flex items-center gap-3">
                      <img
                        src={fotoPreviewObjectUrl || formData.foto}
                        alt=""
                        className="h-20 w-20 rounded-full object-cover border border-gray-200 bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFotoFile(null);
                          setFormData((prev) => ({ ...prev, foto: '' }));
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Quitar imagen
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      if (!/^image\//.test(f.type)) {
                        alert('Elige un archivo de imagen (JPG, PNG, WebP o GIF).');
                        return;
                      }
                      if (f.size > MAX_FOTO_AGENTE_BYTES) {
                        alert('La imagen no debe superar 2 MB.');
                        return;
                      }
                      setFotoFile(f);
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zona asignada
                  </label>
                  <select
                    value={formData.zona_asignada}
                    onChange={(e) => setFormData({...formData, zona_asignada: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar zona</option>
                    {zonas.map(zona => (
                      <option key={zona} value={zona}>{zona}</option>
                    ))}
                    <option value={OPCION_OTRA}>Otra (escribir abajo)</option>
                  </select>
                  {formData.zona_asignada === OPCION_OTRA && (
                    <input
                      type="text"
                      value={zonaOtraValor}
                      onChange={(e) => setZonaOtraValor(e.target.value)}
                      placeholder="Nombre de la nueva zona"
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                {!editandoAgente && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contraseña
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Dejar vacío para asignar después"
                        />
                        <button
                          type="button"
                          onClick={handleGenerarPassword}
                          className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                        >
                          🔐
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Agente activo
                  </label>
                </div>

                <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <input
                      id="visibleEnDirectorio"
                      type="checkbox"
                      checked={formData.visibleEnDirectorio}
                      onChange={(e) =>
                        setFormData({ ...formData, visibleEnDirectorio: e.target.checked })
                      }
                      className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="visibleEnDirectorio" className="text-sm text-gray-700">
                      <span className="font-medium">Visible en página pública /agentes</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Solo se guardan nombre, zona, teléfono y foto. No se expone la colección interna
                        <code className="mx-1">agentes</code>.
                      </span>
                    </label>
                  </div>
                  {formData.visibleEnDirectorio && (
                    <div className="flex items-center pl-6">
                      <input
                        id="mostrarEmailPublico"
                        type="checkbox"
                        checked={formData.mostrarEmailPublico}
                        onChange={(e) =>
                          setFormData({ ...formData, mostrarEmailPublico: e.target.checked })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="mostrarEmailPublico" className="ml-2 text-sm text-gray-700">
                        Mostrar email en la ficha pública
                      </label>
                    </div>
                  )}
                </div>

                {editandoAgente && (
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-800">QR público del agente</div>
                        <div className="text-xs text-gray-600">
                          Apunta a <code className="bg-white/70 px-1 rounded">/agentes</code> y permite verificar identidad.
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleGenerarQrAgente({ regenerateQrOnly: false })}
                          disabled={generandoQr || !formData.visibleEnDirectorio}
                          className="px-3 py-2 text-sm font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300"
                          title={!formData.visibleEnDirectorio ? 'Activa “Visible en página pública /agentes” y guarda' : 'Generar QR'}
                        >
                          {generandoQr ? 'Generando…' : 'Generar QR'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGenerarQrAgente({ regenerateQrOnly: true })}
                          disabled={generandoQr || !formData.visibleEnDirectorio}
                          className="px-3 py-2 text-sm font-semibold rounded-md bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50 disabled:opacity-60"
                          title="Regenerar QR"
                        >
                          Regenerar
                        </button>
                      </div>
                    </div>

                    {qrPublicUrl ? (
                      <div className="text-xs text-gray-700 break-all">
                        URL: <a className="text-indigo-700 underline" href={qrPublicUrl} target="_blank" rel="noreferrer">{qrPublicUrl}</a>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        Sin URL/QR aún. Marca visible en /agentes, guarda y luego genera.
                      </div>
                    )}

                    {qrPreviewUrl ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={qrPreviewUrl}
                          alt="QR público del agente"
                          className="w-24 h-24 rounded-lg border border-indigo-100 bg-white object-contain"
                          loading="lazy"
                        />
                        <a
                          href={qrPreviewUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-indigo-700 font-semibold underline"
                        >
                          Abrir QR
                        </a>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={procesando}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {procesando ? 'Guardando...' : (editandoAgente ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para cambiar clave */}
      {mostrandoModalClave && agenteParaClave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Cambiar Contraseña - {agenteParaClave.nombre}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contraseña
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={nuevaClave}
                    onChange={(e) => setNuevaClave(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={generarClaveParaAgente}
                    className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    🔐
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={cerrarModalClave}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={cambiarClaveAgente}
                disabled={procesando}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {procesando ? 'Cambiando...' : 'Cambiar Contraseña'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar credenciales */}
      {mostrandoModalCredenciales && agenteParaCredenciales && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Asignar Credenciales - {agenteParaCredenciales.nombre}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={credencialesForm.email}
                  onChange={(e) => setCredencialesForm({...credencialesForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={credencialesForm.password}
                    onChange={(e) => setCredencialesForm({...credencialesForm, password: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={generarPasswordAleatoria}
                    className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    🔐
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Contraseña *
                </label>
                <input
                  type="text"
                  value={credencialesForm.confirmPassword}
                  onChange={(e) => setCredencialesForm({...credencialesForm, confirmPassword: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={cerrarModalCredenciales}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={asignarCredencialesAgente}
                disabled={procesando}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {procesando ? 'Asignando...' : 'Asignar Credenciales'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar empresas */}
      {mostrandoModalAsignacion && agenteParaAsignar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Asignar Empresas/Solicitudes - {agenteParaAsignar.nombre}
              </h2>
              <button
                onClick={cerrarModalAsignacion}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona las empresas o solicitudes pendientes que deseas asignar al agente {agenteParaAsignar.nombre}:
                  <br/>
                  <span className="text-xs text-gray-500">Las solicitudes pendientes pasarán a estado "Pendiente de Validación" automáticamente</span>
                </p>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-600">
                    {empresasSeleccionadas.length} de {empresasDisponibles.length} {empresasDisponibles.length === 1 ? 'item' : 'items'} seleccionados
                  </span>
                  <button
                    onClick={() => setEmpresasSeleccionadas(empresasDisponibles.map(e => e.id))}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Seleccionar todas
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {empresasDisponibles.map(empresa => (
                  <div key={empresa.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={empresasSeleccionadas.includes(empresa.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEmpresasSeleccionadas([...empresasSeleccionadas, empresa.id]);
                          } else {
                            setEmpresasSeleccionadas(empresasSeleccionadas.filter(id => id !== empresa.id));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{empresa.nombre}</h3>
                        <p className="text-sm text-gray-500">{empresa.direccion}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            empresa.estado === 'catalogada' 
                              ? 'bg-blue-100 text-blue-800'
                              : empresa.estado === 'pendiente'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {empresa.estado === 'catalogada' ? 'Catalogada' : empresa.estado === 'pendiente' ? 'Pendiente' : empresa.estado || 'Sin estado'}
                          </span>
                          {empresa.tipo === 'solicitud' && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                              Solicitud
                            </span>
                          )}
                          {empresa.tipo === 'empresa' && (
                            <span className="text-xs text-gray-500">Empresa</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {empresasDisponibles.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No hay empresas disponibles para asignar</p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={cerrarModalAsignacion}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={asignarEmpresasSeleccionadas}
                disabled={procesando || empresasSeleccionadas.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {procesando ? 'Asignando...' : `Asignar ${empresasSeleccionadas.length} empresas`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ver empresas asignadas y validar */}
      {mostrandoModalEmpresas && agenteParaEmpresas && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Empresas Asignadas - {agenteParaEmpresas.nombre}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Gestiona y valida las empresas asignadas a este agente
                </p>
              </div>
              <button
                onClick={cerrarModalEmpresas}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {loadingEmpresas ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Cargando empresas...</p>
                </div>
              ) : empresasAsignadasAgente.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Este agente no tiene empresas asignadas</p>
                  <button
                    onClick={() => {
                      cerrarModalEmpresas();
                      abrirModalAsignacion(agenteParaEmpresas);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Asignar ({empresasSeleccionadas.length})
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Estadísticas rápidas */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-2xl font-bold text-blue-800">{empresasAsignadasAgente.length}</div>
                      <div className="text-blue-600 text-sm">Total Asignadas</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-2xl font-bold text-green-800">
                        {empresasAsignadasAgente.filter(e => e.estado === 'activa' || e.validada).length}
                      </div>
                      <div className="text-green-600 text-sm">Validadas</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="text-2xl font-bold text-yellow-800">
                        {empresasAsignadasAgente.filter(e => e.estado === 'pendiente' || e.estado === 'pendiente_validacion' || (!e.validada && e.estado !== 'activa')).length}
                      </div>
                      <div className="text-yellow-600 text-sm">Pendientes</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="text-2xl font-bold text-purple-800">
                        {empresasAsignadasAgente.filter(e => e.visible_en_home).length}
                      </div>
                      <div className="text-purple-600 text-sm">En Home</div>
                    </div>
                  </div>

                  {/* Lista de empresas */}
                  <div className="space-y-3">
                    {empresasAsignadasAgente.map(empresa => (
                      <div 
                        key={empresa.id} 
                        className={`border-2 rounded-lg p-4 ${
                          empresa.estado === 'activa' && empresa.validada
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{empresa.nombre}</h3>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                empresa.estado === 'activa' && empresa.validada
                                  ? 'bg-green-100 text-green-800'
                                  : empresa.estado === 'pendiente_validacion' || empresa.estado === 'pendiente'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {empresa.estado === 'activa' && empresa.validada ? '✅ Validada' :
                                 empresa.estado === 'pendiente_validacion' || empresa.estado === 'pendiente' ? '⏳ Pendiente' :
                                 empresa.estado || 'Sin estado'}
                              </span>
                              {empresa.visible_en_home && (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                  🏠 Visible en Home
                                </span>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                              {empresa.direccion && (
                                <div>
                                  <span className="font-medium">📍 Dirección:</span> {empresa.direccion}
                                </div>
                              )}
                              {empresa.telefono && (
                                <div>
                                  <span className="font-medium">📞 Teléfono:</span> {empresa.telefono}
                                </div>
                              )}
                              {empresa.email && (
                                <div>
                                  <span className="font-medium">📧 Email:</span> {empresa.email}
                                </div>
                              )}
                              {empresa.rubro && (
                                <div>
                                  <span className="font-medium">🏷️ Rubro:</span> {empresa.rubro}
                                </div>
                              )}
                              {empresa.fechaAsignacion && (
                                <div>
                                  <span className="font-medium">📅 Asignada:</span> {
                                    empresa.fechaAsignacion.toDate ? 
                                      empresa.fechaAsignacion.toDate().toLocaleDateString('es-CL') :
                                      new Date(empresa.fechaAsignacion).toLocaleDateString('es-CL')
                                  }
                                </div>
                              )}
                              {empresa.fecha_validacion && (
                                <div>
                                  <span className="font-medium">✅ Validada:</span> {
                                    empresa.fecha_validacion.toDate ? 
                                      empresa.fecha_validacion.toDate().toLocaleDateString('es-CL') :
                                      new Date(empresa.fecha_validacion).toLocaleDateString('es-CL')
                                  }
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="ml-4 flex flex-col gap-2">
                            {(!empresa.validada || empresa.estado !== 'activa') && (
                              <button
                                onClick={() => validarEmpresa(empresa)}
                                disabled={procesando}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
                                title="Validar empresa en terreno y activar perfil"
                              >
                                ✅ Validar y Activar
                              </button>
                            )}
                            {empresa.validada && empresa.estado === 'activa' && (
                              <div className="px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium text-center">
                                ✓ Validada
                              </div>
                            )}
                            <button
                              onClick={() => {
                                if (confirm(`¿Deseas quitar la asignación de "${empresa.nombre}" a este agente?`)) {
                                  updateDoc(doc(db, 'empresas', empresa.id), {
                                    agenteAsignado: null,
                                    fechaAsignacion: null
                                  }).then(() => {
                                    setEmpresasAsignadasAgente(empresasAsignadasAgente.filter(e => e.id !== empresa.id));
                                    cargarAgentes();
                                  });
                                }
                              }}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium whitespace-nowrap"
                              title="Quitar asignación"
                            >
                              🗑️ Quitar
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={cerrarModalEmpresas}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={() => {
                  cerrarModalEmpresas();
                  abrirModalAsignacion(agenteParaEmpresas);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                ➕ Asignar Más Empresas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar agente a solicitud */}
      {mostrandoModalAsignarSolicitud && solicitudSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Asignar Agente a Solicitud
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Empresa: {solicitudSeleccionada.empresaNombre || 'Sin nombre'}
                </p>
              </div>
              <button
                onClick={cerrarModalAsignarSolicitud}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {/* Información de la solicitud */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Información de la Solicitud</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Empresa:</span>
                    <p className="text-gray-900">{solicitudSeleccionada.empresaNombre || 'Sin nombre'}</p>
                  </div>
                  {solicitudSeleccionada.empresaEmail && (
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <p className="text-gray-900">{solicitudSeleccionada.empresaEmail}</p>
                    </div>
                  )}
                  {solicitudSeleccionada.empresaTelefono && (
                    <div>
                      <span className="font-medium text-gray-700">Teléfono:</span>
                      <p className="text-gray-900">{solicitudSeleccionada.empresaTelefono}</p>
                    </div>
                  )}
                  {solicitudSeleccionada.zona && (
                    <div>
                      <span className="font-medium text-gray-700">Zona:</span>
                      <p className="text-gray-900">{solicitudSeleccionada.zona}</p>
                    </div>
                  )}
                  {solicitudSeleccionada.fechaSolicitud && (
                    <div>
                      <span className="font-medium text-gray-700">Fecha Solicitud:</span>
                      <p className="text-gray-900">
                        {solicitudSeleccionada.fechaSolicitud.toDate ? 
                          solicitudSeleccionada.fechaSolicitud.toDate().toLocaleDateString('es-CL', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) :
                          new Date(solicitudSeleccionada.fechaSolicitud).toLocaleDateString('es-CL', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        }
                      </p>
                    </div>
                  )}
                </div>
                {solicitudSeleccionada.notas && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">Notas:</span>
                    <p className="text-gray-900 mt-1">{solicitudSeleccionada.notas}</p>
                  </div>
                )}
              </div>

              {/* Lista de agentes disponibles */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Seleccionar Agente</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona un agente para asignar a esta solicitud. El agente realizará la validación en terreno.
                </p>
                
                {agentes.filter(a => a.activo !== false).length === 0 ? (
                  <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800">No hay agentes activos disponibles</p>
                    <button
                      onClick={() => {
                        cerrarModalAsignarSolicitud();
                        setVistaSeccion('agentes');
                        setTimeout(() => abrirModal(), 100);
                      }}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Crear Nuevo Agente
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {agentes
                      .filter(a => a.activo !== false)
                      .map(agente => (
                        <div
                          key={agente.id}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => asignarAgenteASolicitud(agente.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <h4 className="font-semibold text-gray-900">{agente.nombre}</h4>
                                {agente.zona_asignada && (
                                  <span className="text-xs text-gray-500">📍 {agente.zona_asignada}</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {agente.email && <span>📧 {agente.email}</span>}
                                {agente.telefono && <span className="ml-3">📞 {agente.telefono}</span>}
                              </div>
                              {/* Mostrar empresas asignadas */}
                              {empresasAsignadas?.filter(e => e.agenteAsignado === agente.id).length > 0 && (
                                <div className="text-xs text-gray-500 mt-2">
                                  {empresasAsignadas.filter(e => e.agenteAsignado === agente.id).length} empresas asignadas
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                asignarAgenteASolicitud(agente.id);
                              }}
                              disabled={procesando}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              Asignar
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={cerrarModalAsignarSolicitud}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar agente a empresa específica */}
      {mostrandoModalAsignarEmpresa && empresaParaAsignar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Asignar Agente a Empresa
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Empresa: {empresaParaAsignar.nombre || 'Sin nombre'}
                </p>
              </div>
              <button
                onClick={cerrarModalAsignarEmpresa}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {/* Información de la empresa */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Información de la Empresa</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Nombre:</span>
                    <p className="text-gray-900">{empresaParaAsignar.nombre || 'Sin nombre'}</p>
                  </div>
                  {empresaParaAsignar.email && (
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <p className="text-gray-900">{empresaParaAsignar.email}</p>
                    </div>
                  )}
                  {empresaParaAsignar.telefono && (
                    <div>
                      <span className="font-medium text-gray-700">Teléfono:</span>
                      <p className="text-gray-900">{empresaParaAsignar.telefono}</p>
                    </div>
                  )}
                  {empresaParaAsignar.direccion && (
                    <div>
                      <span className="font-medium text-gray-700">Dirección:</span>
                      <p className="text-gray-900">{empresaParaAsignar.direccion}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de agentes disponibles */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Seleccionar Agente</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona un agente para asignar a esta empresa.
                </p>
                
                {agentes.filter(a => a.activo !== false).length === 0 ? (
                  <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800">No hay agentes activos disponibles</p>
                    <button
                      onClick={() => {
                        cerrarModalAsignarEmpresa();
                        setVistaSeccion('agentes');
                        setTimeout(() => abrirModal(), 100);
                      }}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Crear Nuevo Agente
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {agentes
                      .filter(a => a.activo !== false)
                      .map(agente => (
                        <div
                          key={agente.id}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => asignarEmpresaAAgente(empresaParaAsignar.id, agente.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <h4 className="font-semibold text-gray-900">{agente.nombre}</h4>
                                {agente.zona_asignada && (
                                  <span className="text-xs text-gray-500">📍 {agente.zona_asignada}</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {agente.email && <span>📧 {agente.email}</span>}
                                {agente.telefono && <span className="ml-3">📞 {agente.telefono}</span>}
                              </div>
                              {/* Mostrar empresas asignadas */}
                              {empresasAsignadas?.filter(e => e.agenteAsignado === agente.id).length > 0 && (
                                <div className="text-xs text-gray-500 mt-2">
                                  {empresasAsignadas.filter(e => e.agenteAsignado === agente.id).length} empresas asignadas
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                asignarEmpresaAAgente(empresaParaAsignar.id, agente.id);
                              }}
                              disabled={procesando}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              Asignar
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={cerrarModalAsignarEmpresa}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
